import { PartnerConnectionCheck } from "./PartnerConnectionCheck";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  ClerkProvider,
  HandleSSOCallback,
  RedirectToTasks,
  SignIn,
  SignUp,
  useAuth,
  useClerk,
} from "@clerk/react";
import { getPartnerApp } from "../utils/partnerApp";

function ClerkContent({
  children,
  isConnectionTest,
}: {
  children: ReactNode;
  isConnectionTest: boolean;
}) {
  const clerk = useClerk();
  const auth = useAuth();
  const pathname = window.location.pathname;
  const isAuthForm = pathname === "/login" || pathname === "/signup";

  useEffect(() => {
    if (isAuthForm && auth.isLoaded && auth.isSignedIn) {
      window.location.replace("/auth/continue");
    }
  }, [isAuthForm, auth.isLoaded, auth.isSignedIn]);

  if (isAuthForm && (!auth.isLoaded || auth.isSignedIn)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#F8FAF9] p-5">
        <p role="status" className="text-sm text-[#65777C]">
          {!auth.isLoaded ? "Loading sign-in…" : "Continuing to your account…"}
        </p>
      </main>
    );
  }

  if (clerk.session?.currentTask) return <RedirectToTasks />;

  if (pathname === "/auth/callback") {
    return (
      <HandleSSOCallback
        navigateToApp={({ decorateUrl }) =>
          window.location.replace(decorateUrl("/auth/continue"))
        }
        navigateToSignIn={() =>
          window.location.replace("/auth/continue")
        }
        navigateToSignUp={() =>
          window.location.replace("/auth/continue?mode=signup")
        }
      />
    );
  }

  if (pathname === "/auth/continue") {
    const signup =
      new URLSearchParams(window.location.search).get("mode") === "signup";

    return (
      <main className="flex min-h-screen items-center justify-center bg-[#F8FAF9] p-5">
        <div className="w-full max-w-md">
          {!auth.isLoaded ? (
            <p role="status">Loading sign-in…</p>
          ) : auth.isSignedIn ? (
            <PartnerConnectionCheck />
          ) : signup ? (
            <SignUp
              routing="hash"
              signInUrl="/auth/continue"
              forceRedirectUrl="/auth/continue"
            />
          ) : (
            <SignIn
              routing="hash"
              signUpUrl="/auth/continue?mode=signup"
              forceRedirectUrl="/auth/continue"
            />
          )}
        </div>
      </main>
    );
  }

  return isConnectionTest
    ? <ClerkConnectionPage />
    : <><div id="clerk-captcha" />{children}</>;
}

function ClerkConnectionPage() {
  const auth = useAuth();
  const clerk = useClerk();
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const signup =
    new URLSearchParams(window.location.search).get("mode") === "signup";

  async function signOut() {
    setError("");
    setSigningOut(true);
    try {
      await clerk.signOut({ redirectUrl: "/auth/connect" });
    } catch {
      setError("Unable to sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F8FAF9] p-5">
      <div className="w-full max-w-md space-y-5">
        <header className="text-center">
          <h1 className="text-xl font-semibold text-[#17313A]">
            GluviaCare connection test
          </h1>
          <p className="mt-2 text-sm text-[#65777C]">
            Application: {getPartnerApp()}
          </p>
        </header>

        {!auth.isLoaded ? (
          <p role="status" className="text-center">Loading sign-in…</p>
        ) : auth.isSignedIn ? (
          <section className="space-y-4 rounded-2xl border border-[#E4EFED] bg-white p-6">
            <p role="status">Clerk sign-in is active.</p>
            <p className="text-sm text-[#65777C]">
              Backend account access has not been connected yet.
            </p>
            {error && <p role="alert">{error}</p>}
            <button
              type="button"
              onClick={() => void signOut()}
              disabled={signingOut}
              className="w-full rounded-lg bg-[#159A92] px-4 py-3 text-sm font-medium text-white hover:bg-[#117E78] disabled:opacity-50"
            >
              {signingOut ? "Signing out…" : "Sign out of Clerk"}
            </button>
          </section>
        ) : signup ? (
          <SignUp
            routing="hash"
            signInUrl="/auth/connect"
            forceRedirectUrl="/auth/connect"
          />
        ) : (
          <SignIn
            routing="hash"
            signUpUrl="/auth/connect?mode=signup"
            forceRedirectUrl="/auth/connect"
          />
        )}
      </div>
    </main>
  );
}

export function ClerkRoot({ children }: { children: ReactNode }) {
  const publishableKey =
    import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim();
  const isConnectionTest =
    import.meta.env.DEV &&
    window.location.pathname === "/auth/connect";

  if (!publishableKey) {
    return isConnectionTest ? (
      <main className="p-6">
        <p role="alert">Sign-in configuration is unavailable.</p>
      </main>
    ) : <main className="p-6"><p role="alert">Sign-in configuration is unavailable. Please contact support.</p></main>;
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl="/login"
      signUpUrl="/signup"
      afterSignOutUrl="/"
      appearance={{
        variables: {
          colorPrimary: "#159A92",
          borderRadius: "0.75rem",
        },
      }}
    >
      <ClerkContent isConnectionTest={isConnectionTest}>{children}</ClerkContent>
    </ClerkProvider>
  );
}
